import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { getSearchHistory, getHistoryCount, removeFromHistory, clearHistory } from '../utils/searchHistory.js';
import { useSidebar } from '../contexts/SidebarContext.jsx';
import InsightsSidebar from './InsightsSidebar.jsx';
import '../styles/history-insights.css';

const HistoryInsightsView = ({ isAuthenticated }) => {
    // --- Use Sidebar Context ---
    const {
        activeItem,
        isOpen: contextIsOpen,
        hasUserClosed: contextHasUserClosed,
        setActiveItem,
        setIsOpen: setContextIsOpen,
        setUserClosed: setContextUserClosed,
        userPreferences
    } = useSidebar();
    
    // --- Subscription Check ---
    const userTier = window.userTier || 'anonymous';
    const isLoggedIn = window.isLoggedIn || false;
    
    // Debug logging
    console.log('🔍 HistoryInsightsView - Debug Info:', {
        userTier,
        isLoggedIn,
        windowUserTier: window.userTier,
        windowIsLoggedIn: window.isLoggedIn
    });
    
    // Tier hierarchy - history insights require professional or higher
    const tierLevels = {
        'anonymous': 0,
        'free': 1,
        'professional': 2,
        'enterprise': 3
    };
    
    const userLevel = tierLevels[userTier] || 0;
    const hasHistoryInsightsAccess = userLevel >= tierLevels.professional;
    
    console.log('🔍 HistoryInsightsView - Access Check:', {
        userLevel,
        requiredLevel: tierLevels.professional,
        hasHistoryInsightsAccess
    });
    
    const [history, setHistory] = useState({ keywords: [], companies: [] });
    const [historyCount, setHistoryCount] = useState({ keywords: 0, companies: 0, total: 0 });
    const [hasManualSelection, setHasManualSelection] = useState(false);
    const [isOpen, setIsOpen] = useState(contextIsOpen);
    const [hasUserClosed, setHasUserClosed] = useState(contextHasUserClosed);

    // Sidebar width & resizing state (desktop only)
    const DEFAULT_WIDTH = 500;
    const MIN_WIDTH = 100;
    const MAX_WIDTH = 700;
    const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_WIDTH);
    const [isDragging, setIsDragging] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    
    // Saved keywords state
    const [savedKeywords, setSavedKeywords] = useState(new Set());
    const [savingKeywords, setSavingKeywords] = useState(new Set());

    // Load history on component mount and set up refresh listener
    const loadHistory = useCallback(() => {
        const currentHistory = getSearchHistory();
        const counts = getHistoryCount();
        setHistory(currentHistory);
        setHistoryCount(counts);
    }, []);

    useEffect(() => {
        loadHistory();

        // Listen for history updates from other components
        const handleHistoryUpdate = () => {
            loadHistory();
        };

        // Listen for new items being added to history
        const handleNewHistoryItem = (event) => {
            const { type, item } = event.detail;
            console.log(`HistoryInsightsView: New ${type} added to history:`, item);
            
            // Force open the sidebar and reset manual selection flag for new items
            setHasUserClosed(false);
            setIsOpen(true);
            setHasManualSelection(false); // Reset manual flag so new item can be auto-selected
            
            // Load updated history
            loadHistory();
        };

        window.addEventListener('searchHistoryUpdated', handleHistoryUpdate);
        window.addEventListener('newHistoryItem', handleNewHistoryItem);
        return () => {
            window.removeEventListener('searchHistoryUpdated', handleHistoryUpdate);
            window.removeEventListener('newHistoryItem', handleNewHistoryItem);
        };
    }, [loadHistory]);

    // Sync local open/closed state with context (hydrates from persisted state on route change)
    useEffect(() => {
        setIsOpen(contextIsOpen);
    }, [contextIsOpen]);
    useEffect(() => {
        setHasUserClosed(contextHasUserClosed);
    }, [contextHasUserClosed]);

    // Load saved keywords on mount (only when sidebar is open)
    const loadSavedKeywords = useCallback(async () => {
        if (!isAuthenticated || !isOpen) return;
        
        try {
            const response = await fetch('/user_keywords');
            if (response.ok) {
                const data = await response.json();
                const keywordSet = new Set(data.keywords.map(k => k.keyword));
                setSavedKeywords(keywordSet);
            }
        } catch (error) {
            console.error('Error loading saved keywords:', error);
        }
    }, [isAuthenticated, isOpen]);

    // Save keyword to user's watchlist
    const saveKeywordToWatchlist = useCallback(async (keyword) => {
        if (!isAuthenticated) {
            alert('Please log in to save keywords to your watchlist');
            return;
        }

        const keywordName = keyword.keyword_name || keyword.name || keyword;
        
        // Check if already saving
        if (savingKeywords.has(keywordName)) return;
        
        // Check if already saved
        if (savedKeywords.has(keywordName)) {
            showToast('Keyword already in your watchlist', 'info');
            return;
        }

        setSavingKeywords(prev => new Set([...prev, keywordName]));

        try {
            const response = await fetch('/user_keywords', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    keyword: keywordName,
                    data_sources: ['search']
                })
            });

            if (response.ok) {
                setSavedKeywords(prev => new Set([...prev, keywordName]));
                showToast(`"${keywordName}" saved to watchlist!`, 'success');
            } else {
                const error = await response.json();
                showToast(error.error || 'Failed to save keyword', 'error');
            }
        } catch (error) {
            console.error('Error saving keyword:', error);
            showToast('Failed to save keyword', 'error');
        } finally {
            setSavingKeywords(prev => {
                const newSet = new Set(prev);
                newSet.delete(keywordName);
                return newSet;
            });
        }
    }, [isAuthenticated, savedKeywords, savingKeywords]);

    // Simple toast notification
    const showToast = useCallback((message, type = 'info') => {
        const toast = document.createElement('div');
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${type === 'success' ? '#22c55e' : type === 'error' ? '#ef4444' : '#3b82f6'};
            color: white;
            padding: 12px 16px;
            border-radius: 8px;
            z-index: 10000;
            font-size: 14px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
            if (document.body.contains(toast)) {
                document.body.removeChild(toast);
            }
        }, 3000);
    }, []);

    // Load saved keywords when authenticated and sidebar is open
    useEffect(() => {
        loadSavedKeywords();
    }, [loadSavedKeywords]);

    // Auto-select the most recent item when history changes (only for initial load or no manual selection)
    useEffect(() => {
        // Don't auto-select if sidebar is closed to prevent unnecessary data fetching
        if (!isOpen) {
            console.log('🚫 Sidebar closed - skipping auto-selection');
            return;
        }

        const allItems = [...history.keywords, ...history.companies];
        if (allItems.length > 0) {
            const mostRecent = allItems.reduce((latest, item) => {
                return item.timestamp > (latest?.timestamp || 0) ? item : latest;
            }, null);
            
            // Only auto-select if:
            // 1. No manual selection has been made AND
            // 2. (No current selection OR this is a newer item than currently selected)
            const shouldAutoSelect = !hasManualSelection && 
                (!activeItem || (mostRecent && mostRecent.timestamp > (activeItem.data?.timestamp || 0)));
            
            if (mostRecent && shouldAutoSelect) {
                // Determine if it's a keyword or company
                const isKeyword = history.keywords.includes(mostRecent);
                
                console.log("HistoryInsightsView: Auto-selecting most recent item:", mostRecent);
                
                // Clear the chart first to start fresh
                window.dispatchEvent(new CustomEvent('resetUI', { detail: { source: 'autoSelect' } }));
                
                // Create the selection without triggering manual flag
                setActiveItem({ type: isKeyword ? 'keyword' : 'company', data: mostRecent });
                
                // Ensure sidebar is open for new selections
                if (userPreferences?.autoExpand && !isOpen && !hasUserClosed) {
                    setIsOpen(true);
                }
                
                // Small delay to ensure chart is cleared before loading new data
                setTimeout(() => {
                    // Dispatch events to update InsightsSidebar
                    if (isKeyword) {
                        window.dispatchEvent(new CustomEvent('keywordSelected', { detail: mostRecent }));
                        window.dispatchEvent(new CustomEvent('triggerKeywordSearch', { detail: { keyword: mostRecent.keyword_name } }));
                    } else {
                        window.dispatchEvent(new CustomEvent('companySelected', { detail: mostRecent }));
                        window.dispatchEvent(new CustomEvent('triggerCompanySearch', { detail: mostRecent }));
                    }
                }, 100);
            }
        }
    }, [history, activeItem, hasManualSelection, isOpen, hasUserClosed, userPreferences?.autoExpand]);

    // Handle selecting a keyword from history
    const handleKeywordSelect = useCallback((keyword) => {
        const newItem = { type: 'keyword', data: keyword };
        setActiveItem(newItem);
        setHasManualSelection(true); // Mark as manual selection
        
        // Clear the chart first to start fresh
        console.log("HistoryInsightsView: Clearing chart before selecting keyword:", keyword.keyword_name);
        window.dispatchEvent(new CustomEvent('resetUI', { detail: { source: 'keywordSelect' } }));
        
        // Small delay to ensure chart is cleared before loading new data
        setTimeout(() => {
            // Dispatch event to update InsightsSidebar
            console.log("HistoryInsightsView: Selecting keyword from history:", keyword);
            window.dispatchEvent(new CustomEvent('keywordSelected', { detail: keyword }));
            
            // Also trigger keyword search for chart loading
            window.dispatchEvent(new CustomEvent('triggerKeywordSearch', { detail: { keyword: keyword.keyword_name } }));
        }, 100);
    }, [setActiveItem]);

    // Handle selecting a company from history
    const handleCompanySelect = useCallback((company) => {
        const newItem = { type: 'company', data: company };
        setActiveItem(newItem);
        setHasManualSelection(true); // Mark as manual selection
        
        // Clear the chart first to start fresh
        console.log("HistoryInsightsView: Clearing chart before selecting company:", company.symbol);
        window.dispatchEvent(new CustomEvent('resetUI', { detail: { source: 'companySelect' } }));
        
        // Small delay to ensure chart is cleared before loading new data
        setTimeout(() => {
            // Dispatch event to update InsightsSidebar
            console.log("HistoryInsightsView: Selecting company from history:", company);
            window.dispatchEvent(new CustomEvent('companySelected', { detail: company }));
            
            // Also trigger company search for chart loading
            window.dispatchEvent(new CustomEvent('triggerCompanySearch', { detail: company }));
        }, 100);
    }, [setActiveItem]);

    // Handle removing an item from history
    const handleRemoveItem = useCallback((type, item) => {
        const id = type === 'keyword' ? item.id : item.company_id;
        removeFromHistory(type === 'keyword' ? 'keywords' : 'companies', id);
        
        // If the removed item was selected, clear selection and reset manual flag
        if (activeItem && 
            ((type === 'keyword' && activeItem.data.id === id) ||
             (type === 'company' && activeItem.data.company_id === id))) {
            setActiveItem(null);
            setHasManualSelection(false);
            window.dispatchEvent(new CustomEvent('resetUI', { detail: { source: 'historyRemove' } }));
        }
        
        loadHistory();
    }, [activeItem, loadHistory]);

    // Handle clearing all history
    const handleClearHistory = useCallback(() => {
        clearHistory();
        setActiveItem(null);
        setHasManualSelection(false); // Reset manual selection flag
        loadHistory();
        window.dispatchEvent(new CustomEvent('resetUI', { detail: { source: 'historyClear' } }));
    }, [loadHistory]);

    // --- Sidebar Toggle Handlers ---
    const toggleSidebar = useCallback(() => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        setHasUserClosed(!newIsOpen);
        setContextIsOpen(newIsOpen);
        setContextUserClosed(!newIsOpen);
    }, [isOpen, setContextIsOpen, setContextUserClosed]);

    const closeSidebar = useCallback(() => {
        console.log('HistoryInsightsView: Closing sidebar');
        setHasUserClosed(true);
        setIsOpen(false);
        setContextUserClosed(true);
        setContextIsOpen(false);
    }, [setContextIsOpen, setContextUserClosed]);

    // Auto-open sidebar when history has items (only if user preference allows and user hasn't closed it)
    useEffect(() => {
        if (userPreferences?.autoExpand && historyCount.total > 0 && !isOpen && !hasUserClosed) {
            setIsOpen(true);
        }
    }, [userPreferences?.autoExpand, historyCount.total, isOpen, hasUserClosed]);

    // Add/remove body class when sidebar opens/closes
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('sidebar-open');
        } else {
            document.body.classList.remove('sidebar-open');
        }
        
        // Cleanup on unmount
        return () => {
            document.body.classList.remove('sidebar-open');
        };
    }, [isOpen]);

    // Reflect current sidebar width to a global CSS variable so pages can adjust layout
    useEffect(() => {
        const applyWidthVar = () => {
            const targetWidth = isOpen && !isMobile
                ? Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, sidebarWidth))
                : 0;
            try {
                document.documentElement.style.setProperty('--history-sidebar-width', `${targetWidth}px`);
            } catch (_) {}
        };
        applyWidthVar();
        return () => {
            // On unmount, reset to 0 to avoid affecting other pages
            try { document.documentElement.style.setProperty('--history-sidebar-width', '0px'); } catch (_) {}
        };
    }, [isOpen, isMobile, sidebarWidth]);

    // Initialize width and mobile flag
    useEffect(() => {
        // In development, check if DEFAULT_WIDTH changed and reset if needed
        if (process.env.NODE_ENV === 'development') {
            const lastDefaultWidth = localStorage.getItem('lastDefaultWidth');
            if (lastDefaultWidth && parseInt(lastDefaultWidth) !== DEFAULT_WIDTH) {
                localStorage.removeItem('historySidebarWidth');
                console.log('🔧 Dev: Sidebar width reset due to DEFAULT_WIDTH change');
            }
            localStorage.setItem('lastDefaultWidth', String(DEFAULT_WIDTH));
        }
        
        const stored = parseInt(localStorage.getItem('historySidebarWidth'), 10);
        if (!isNaN(stored)) {
            setSidebarWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, stored)));
        }

        const updateMobile = () => setIsMobile(window.innerWidth <= 768);
        updateMobile();
        window.addEventListener('resize', updateMobile);
        return () => window.removeEventListener('resize', updateMobile);
    }, []);

    // Clamp width if window gets too small
    useEffect(() => {
        if (isMobile) return;
        const maxAllowed = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - 120));
        if (sidebarWidth > maxAllowed) setSidebarWidth(maxAllowed);
    }, [isMobile, sidebarWidth]);

    // Drag handlers (left edge of right-fixed sidebar)
    const handleResizeMouseDown = useCallback((e) => {
        if (isMobile) return; // disable on mobile
        setIsDragging(true);
        e.preventDefault();
        e.stopPropagation();

        const onMouseMove = (ev) => {
            // Width = distance from cursor to right edge of viewport
            const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, window.innerWidth - ev.clientX));
            setSidebarWidth(newWidth);
            try { document.documentElement.style.setProperty('--history-sidebar-width', `${newWidth}px`); } catch (_) {}
        };

        const onMouseUp = () => {
            setIsDragging(false);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            try { localStorage.setItem('historySidebarWidth', String(sidebarWidth)); } catch (_) {}
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [isMobile, sidebarWidth]);

    // Keyboard support for closing sidebar
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isOpen) {
                closeSidebar();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, closeSidebar]);

    // Format timestamp for display
    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return date.toLocaleDateString();
    };

    // Render compact item chip
    const renderHistoryItem = (item, type, onSelect, onRemove) => {
        const isSelected = activeItem && 
            ((type === 'keyword' && activeItem.data.id === item.id) ||
             (type === 'company' && activeItem.data.company_id === item.company_id));

        return (
            <div
                key={item.id || item.company_id}
                className={`history-item inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all duration-200 ${isSelected ? 'selected' : ''}`}
                title={type === 'keyword' ? item.keyword_name : `${item.symbol}${item.name && item.name !== item.symbol ? ` - ${item.name}` : ''}`}
            >
                {/* Content */}
                {type === 'keyword' ? (
                    <span 
                        className="item-label font-medium truncate max-w-24 cursor-pointer transition-colors"
                        onClick={() => onSelect(item)}
                    >
                        {item.keyword_name}
                    </span>
                ) : (
                    <div 
                        className="flex items-center gap-1 min-w-0 cursor-pointer transition-colors"
                        onClick={() => onSelect(item)}
                    >
                        <span className="font-medium flex-shrink-0">
                            {item.symbol}
                        </span>
                        {item.name && item.name !== item.symbol && (
                            <span className="text-gray-400 truncate max-w-20 text-[10px] item-label">
                                {item.name}
                            </span>
                        )}
                    </div>
                )}

                {/* Save button for keywords only */}
                {type === 'keyword' && isAuthenticated && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            saveKeywordToWatchlist(item);
                        }}
                        className={`save-keyword-button flex-shrink-0 p-0.5 ml-1 transition-all duration-200 ${
                            savedKeywords.has(item.keyword_name) 
                                ? 'text-green-400 cursor-default' 
                                : savingKeywords.has(item.keyword_name)
                                    ? 'text-yellow-400 animate-pulse cursor-wait'
                                    : 'text-gray-400 hover:text-white cursor-pointer'
                        }`}
                        disabled={savedKeywords.has(item.keyword_name) || savingKeywords.has(item.keyword_name)}
                        title={
                            savedKeywords.has(item.keyword_name) 
                                ? 'Already saved to watchlist' 
                                : savingKeywords.has(item.keyword_name)
                                    ? 'Saving...'
                                    : 'Save to watchlist'
                        }
                        aria-label={`Save ${item.keyword_name} to watchlist`}
                    >
                        {savedKeywords.has(item.keyword_name) ? (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        ) : savingKeywords.has(item.keyword_name) ? (
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                            </svg>
                        )}
                    </button>
                )}

                {/* Remove button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(type, item);
                    }}
                    className="history-remove-button flex-shrink-0 p-0.5 ml-1"
                    aria-label={`Remove ${type === 'keyword' ? item.keyword_name : item.symbol} from history`}
                >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>
        );
    };

    // If user doesn't have history insights access, show upgrade prompt and ensure no space is reserved
    if (!hasHistoryInsightsAccess) {
        // Ensure layout does not reserve sidebar space for free users
        try { document.documentElement.style.setProperty('--history-sidebar-width', '0px'); } catch (_) {}
        return (
            <>
                {/* Single Toggle/Close Button - Opens upgrade popup */}
                {(typeof document !== 'undefined' && createPortal(
                    <button 
                        type="button"
                        className={`history-toggle ${isOpen ? 'open' : ''}`}
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            // Open upgrade popup instead of sidebar
                            if (window.openSubscriptionPopup) {
                                window.openSubscriptionPopup();
                            } else {
                                window.location.href = '/find-your-plan';
                            }
                        }}
                        title="Upgrade to Access History & Insights"
                        aria-label="Upgrade to Access History & Insights"
                        style={{ pointerEvents: 'auto' }}
                    >
                        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" width="40" height="40">
                            <path fill="none" stroke="currentColor" strokeWidth="2" d="M1,22 L23,22 L23,2 L1,2 L1,22 Z M16,2 L16,22 L16,2 Z"/>
                        </svg>
                    </button>,
                    document.body
                )) || (
                    <button 
                        type="button"
                        className={`history-toggle ${isOpen ? 'open' : ''}`}
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            // Open upgrade popup instead of sidebar
                            if (window.openSubscriptionPopup) {
                                window.openSubscriptionPopup();
                            } else {
                                window.location.href = '/find-your-plan';
                            }
                        }}
                        title="Upgrade to Access History & Insights"
                        aria-label="Upgrade to Access History & Insights"
                    >
                        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" width="40" height="40">
                            <path fill="none" stroke="currentColor" strokeWidth="2" d="M1,22 L23,22 L23,2 L1,2 L1,22 Z M16,2 L16,22 L16,2 Z"/>
                        </svg>
                    </button>
                )}
            </>
        );
    }

    return (
        <>
            {/* Single Toggle/Close Button */}
            {(typeof document !== 'undefined' && createPortal(
                <button 
                    type="button"
                    className={`history-toggle ${isOpen ? 'open' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleSidebar(); }}
                    title={isOpen ? "Close Sidebar" : "Open Sidebar"}
                    aria-label={isOpen ? "Close Sidebar" : "Open Sidebar"}
                    style={{ pointerEvents: 'auto' }}
                >
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" width="40" height="40">
                        <path fill="none" stroke="currentColor" strokeWidth="2" d="M1,22 L23,22 L23,2 L1,2 L1,22 Z M16,2 L16,22 L16,2 Z"/>
                    </svg>
                </button>,
                document.body
            )) || (
                <button 
                    type="button"
                    className={`history-toggle ${isOpen ? 'open' : ''}`}
                    onClick={(e) => { e.stopPropagation(); toggleSidebar(); }}
                    title={isOpen ? "Close Sidebar" : "Open Sidebar"}
                    aria-label={isOpen ? "Close Sidebar" : "Open Sidebar"}
                >
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" width="40" height="40">
                        <path fill="none" stroke="currentColor" strokeWidth="2" d="M1,22 L23,22 L23,2 L1,2 L1,22 Z M16,2 L16,22 L16,2 Z"/>
                    </svg>
                </button>
            )}

            {/* Backdrop (mobile only) */}
            <div 
                className={`history-backdrop ${isOpen ? 'visible' : ''}`}
                onClick={(e) => { e.stopPropagation(); closeSidebar(); }}
                role="button"
                aria-label="Close Sidebar Backdrop"
            />

            {/* History Sidebar */}
            <div 
                className={`history-insights-view ${isOpen ? 'open' : ''}`}
                style={{ width: isMobile ? undefined : `${Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, sidebarWidth))}px` }}
            >

                {/* Resize handle (left edge) - desktop only */}
                {!isMobile && (
                    <div 
                        className={`history-resize-handle${isDragging ? ' dragging' : ''}`}
                        onMouseDown={handleResizeMouseDown}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize sidebar"
                        title="Drag to resize"
                    />
                )}

                {/* Scrollable Content Container */}
                <div className="sidebar-scrollable-content">
                    {/* History Bar - Horizontal at top */}
                    <div className="history-bar bg-white/5 border-b border-white/10 p-2">
                        {/* Header with Clear All button */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center justify-center w-full">
                                <h2 style={{
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    color: '#ffffff',
                                    letterSpacing: '0.025em',
                                    textTransform: 'uppercase'
                                }}>
                                    Recent Activity
                                </h2>
                            </div>
                            {historyCount.total > 0 && (
                                <button
                                    onClick={handleClearHistory}
                                    className="text-xs text-gray-400 hover:text-red-400 transition-colors font-medium"
                                >
                                    Clear All
                                </button>
                            )}
                        </div>

                {/* History Content */}
                {historyCount.total === 0 ? (
                    <div className="text-center py-6">
                        <div style={{ 
                            color: 'rgba(255, 255, 255, 0.25)', 
                            fontSize: '0.75rem',
                            fontWeight: '400',
                            letterSpacing: '0.01em'
                        }}>
                            No search history yet
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        {/* Keywords Section - Left Side */}
                        <div className="keywords-section">
                            <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                                <h3 className="text-sm font-medium text-gray-300">
                                    Keywords ({history.keywords.length})
                                </h3>
                            </div>
                            <div className="max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent history-list">
                                {history.keywords.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {history.keywords.map((keyword) => 
                                            renderHistoryItem(keyword, 'keyword', handleKeywordSelect, handleRemoveItem)
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ color: 'rgba(255, 255, 255, 0.2)', fontSize: '0.7rem', padding: '0.5rem 0' }}>No keywords yet</div>
                                )}
                            </div>
                        </div>

                        {/* Companies Section - Right Side */}
                        <div className="companies-section">
                            <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <h3 className="text-sm font-medium text-gray-300">
                                    Companies ({history.companies.length})
                                </h3>
                            </div>
                            <div className="max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent history-list">
                                {history.companies.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {history.companies.map((company) => 
                                            renderHistoryItem(company, 'company', handleCompanySelect, handleRemoveItem)
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ color: 'rgba(255, 255, 255, 0.2)', fontSize: '0.7rem', padding: '0.5rem 0' }}>No companies yet</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                    </div>

                    {/* Insights Panel - Stacked below history */}
                    <div className="insights-panel-stacked">
                        <InsightsSidebar 
                            isAuthenticated={isAuthenticated} 
                            isStacked={true}
                            parentIsOpen={isOpen}
                            onParentClose={closeSidebar}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

HistoryInsightsView.propTypes = {
    isAuthenticated: PropTypes.bool.isRequired
};

export default HistoryInsightsView; 